<script setup>
import { setMetaData } from '@/plugins/misc'
import { mediaUrl } from '@core/utils/mediaUrl'
import { useReservationsStore, useUserGroupsStore, useUsersStore } from "@/stores"
import moment from 'moment'
import { computed, onMounted, ref } from 'vue'
import { storeToRefs } from 'pinia'
import UpgradeProfileDialog from '@core/components/UpgradeProfileDialog.vue'
import { membershipEndDate, membershipMessage, upcomingMemberships } from '@core/utils/memberships'
import { EXTENSION_PRICE_DOLLARS, REGULAR_ANNUAL_PRICE_DOLLARS } from '@/utils/membershipPricing'

// Initialize stores
const usersStore = useUsersStore()
const userGroupsStore = useUserGroupsStore()
const reservationsStore = useReservationsStore()

// Use storeToRefs for reactive store data
const { myUser, isShowingPreferredRequestInfoDialog } = storeToRefs(usersStore)
const { guestUserGroup, userGroups } = storeToRefs(userGroupsStore)
const { myReservations } = storeToRefs(reservationsStore)

const platinumSelection = ref()

const isUserLoggedIn = localStorage.getItem('userData') ? true : false

// Upgrade profile dialog
const showUpgradeDialog = ref(false)
const upgradeProductId = ref(null)
const upgradePriceDollars = ref(null)
const upgradeMessage = ref(null)
const upgradeIsExtension = ref(false)

const requestUpgrade = (productId, priceDollars, seats, discountGivenDollars, message, isExtension = false) => {
  upgradeProductId.value = productId
  upgradePriceDollars.value = priceDollars
  upgradeMessage.value = message
  upgradeIsExtension.value = isExtension
  showUpgradeDialog.value = true
}

const onUpgradeDialogComplete = async () => {
  showUpgradeDialog.value = false
  upgradeIsExtension.value = false

  // Payment was handled inline - just refresh user data
  await usersStore.getMyUser()
}

const onUpgradeDialogCancel = () => {
  showUpgradeDialog.value = false
  upgradeIsExtension.value = false
}

const homeIconPath = computed(() => {
  if (guestUserGroup.value?.joinUrl)
    return guestUserGroup.value.joinUrl
  
  return '/home/join'
})

const formatDate = theMoment => {
  return theMoment.format("M/D/YY")
}

const platinumLastActivePaidThroughDateString = computed(() => {
  let theMoment = moment(myUser?.value?.platinumSubscriptionsByState?.lastActive.paidThroughDate)
  
  return formatDate(theMoment)
})

const platinumExpirationDateString = computed(() => {
  let theMoment = moment(myUser?.value?.platinumRenewalDate).subtract(1, 'day')

  return formatDate(theMoment)
})

const isExtensionEligible = computed(() => {
  return myUser?.value?.isPlatinum && myUser?.value?.platinumRenewalDate && moment(myUser.value.platinumRenewalDate).isAfter(moment())
})

const extensionRateExpirationDateString = computed(() => {
  if (myUser?.value?.platinumRenewalDate) {
    return moment(myUser.value.platinumRenewalDate).subtract(1, 'day').format("M/D/YY")
  }
  
  return null
})

const divOrderStandard = computed(() => {
  if (!myUser?.value)
    return 1
  if (myUser?.value?.membershipType == 'Standard')
    return 1
  if (myUser?.value?.membershipType == 'Trial')
    return 6
  if (myUser?.value?.membershipType == 'Premium')
    return 5
  if (myUser?.value?.isPlatinum)
    return 5
})

const divOrderPlatinumGroup = computed(() => {
  if (!myUser?.value)
    return 3
  if (myUser?.value?.membershipType == 'Standard')
    return 5
  if (myUser?.value?.membershipType == 'Trial')
    return 4
  if (myUser?.value?.membershipType == 'Premium')
    return 4
  if (myUser?.value?.isPlatinum)
    return 1
})

// Computed properties
const optionsForPlatinum = computed(() => {
  const isExtension = myUser?.value?.isPlatinum && myUser?.value?.platinumRenewalDate && moment(myUser.value.platinumRenewalDate).isAfter(moment())
  const oneYearPrice = isExtension ? EXTENSION_PRICE_DOLLARS : REGULAR_ANNUAL_PRICE_DOLLARS
  const oneYearProductId = isExtension ? 'individual-1cycle-annual-extension' : 'individual-1cycle-annual'

  const returnArray = [
    {
      title: '$' + oneYearPrice + '/1 year',
      priceDollars: oneYearPrice,
      price: '$' + oneYearPrice,
      period: '1 year',
      periodAbbreviation: 'yr',
      productId: oneYearProductId,
      description: isExtension ? "One Year Platinum Extension" : "One Year Platinum",
      seats: 1
    }
  ]

  // Set default selection logic (from original commit #93)
  if (platinumSelection.value == null || (platinumSelection.value && !returnArray.some(item => item.title === platinumSelection.value?.title))) {
    if (returnArray[2]) {
      platinumSelection.value = returnArray[2]
    } else {
      platinumSelection.value = returnArray[0]
    }
  }

  return returnArray
})

const platinumAccountName = computed(() => {
  if (myUser?.primaryGroupPermission == 'owner') {
    if (currentNumberOfPaidSeats?.value >= 33) {
      // seatPriceDollars.value = 100
      return "Platinum 50"
    }
    if (currentNumberOfPaidSeats?.value >= 5) {
      // seatPriceDollars.value = 150
      return "Platinum 5"
    }
    if (currentNumberOfPaidSeats?.value >= 1) {
      // seatPriceDollars.value = 250
      return "Platinum 1"
    }
  } else {
    return "Platinum"
  }

})

const platinumExtendsToMoment = computed(() => {
  if (platinumSelection?.value?.period) {
    let parts = platinumSelection.value.period.split(" ") // split the string into an array ["3", "months"]

    // parts[0] is "3" and parts[1] is "months"

    let num = parseInt(parts[0])  // convert the string "3" into the number 3
    let unit = parts[1]  // "months"

    if (myUser?.value?.platinumRenewalDate)
      return moment(myUser?.value?.platinumRenewalDate).subtract(1, 'day').add(num, unit)
    else
      return moment().subtract(1, 'day').add(num, unit)
  }
})

const primaryGroupProfileImageId = computed(() => {
  if (myUser?.value?.primaryGroupName) {
    //find the group in userGroups that matches the primaryGroupName
    let primaryGroup = userGroups?.value?.find(group => group.name === myUser?.value?.primaryGroupName)
    if (primaryGroup) {
      return primaryGroup.profileImageId
    }
  }
  
  return null
})

const membershipDisplayName = computed(() => {
  if (!myUser?.value) return null
  if (myUser.value.membershipType == 'Standard') return 'Free Tier'
  if (myUser.value.membershipType == 'Trial') return 'Trial'
  if (myUser.value.membershipType == 'Premium') return 'Premium'
  if (myUser.value.isPlatinum) return platinumAccountName.value || 'Platinum'
  
  return 'Standard'
})

const currentMembershipMessage = computed(() =>
  membershipMessage(myUser?.value?.platinumSubscriptionsByState?.current))

// Every upcoming membership, not just the one `platinumSubscriptionsByState`
// keeps — memberships stack back to back, and that virtual holds a single
// pending slot on a last-wins loop, so the middle of a stack disappears.
const upcomingSubscriptions = computed(() =>
  upcomingMemberships(myUser?.value?.subscriptionsAll?.platinum))

// Prior memberships, most recent first. `status === 'Complete'` is what makes a
// membership prior; the current and upcoming ones are rendered above.
const priorSubscriptions = computed(() =>
  (myUser?.value?.subscriptionsAll?.platinum || [])
    .filter(s => s?.status === 'Complete')
    .slice()
    .reverse())

const currentNumberOfPaidSeats = computed(() => {
  return myUser?.value?.platinumSubscriptionsByState?.pending?.numberOfSeats || myUser?.value?.platinumSubscriptionsByState?.current?.numberOfSeats || 0
})

onMounted(async () => {
  await usersStore.getMyUser()
})
</script>

<template>
  <div>
    <UpgradeProfileDialog
      v-model="showUpgradeDialog"
      :product-id="upgradeProductId"
      :price-dollars="upgradePriceDollars"
      :message="upgradeMessage"
      :is-extension="upgradeIsExtension"
      @complete="onUpgradeDialogComplete"
      @cancel="onUpgradeDialogCancel"
    />

    <!-- Back to Restaurant Admin Button -->
    <div
      v-if="myUser?.profileId"
      class="content-wrapper mt-6 mb-n4"
    >
      <div class="back-button-container">
        <RouterLink
          :to="`/m/${myUser?.profileId}`"
          class="back-button d-flex align-center cursor-pointer"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="m15 18-6-6 6-6" />
          </svg>
          <span class="ml-1">Back to Profile</span>
        </RouterLink>
      </div>
    </div>

    <section class="alternate-masthead">
      <div class="content-wrapper">
        <div class="masthead-content">
          <span class=" masthead-content--row justify-center">
            <h2
              v-if="myUser && membershipDisplayName"
              class="black-text masthead-content-headline"
            >Membership: {{ membershipDisplayName }}</h2>
            <h2
              v-else-if="myUser"
              class="black-text masthead-content-headline"
            >Membership</h2>
            <h2
              v-else
              class="black-text masthead-content-headline"
            >Membership Tiers</h2>
          </span>

          <VBtn
            v-if="!isUserLoggedIn"
            class="mt-10"
            size="large"
            :to="homeIconPath"
          >
            Request Access
          </VBtn>
          <VRow
            v-else
            class="mt-0"
            align="center"
          >
            <VCol
              v-if="primaryGroupProfileImageId"
              cols="auto"
              class="pl-4"
              style="max-width: 120px;"
            >
              <VImg
                class="avatar-image"
                :src="mediaUrl(primaryGroupProfileImageId, 'small')"
                height="80"
              />
            </VCol>
            <VCol
              class="align align-self-center "
              style="text-align: left;"
            >
              <!-- For Free -->
              <template v-if="myUser?.membershipType == 'Standard'">
                <div class="d-flex flex-column align-center">
                  <div
                    v-if="myUser.platinumMembershipStatus == 'expired'"
                    class="black-text text-sm"
                  >
                    (Platinum Term exp {{ platinumLastActivePaidThroughDateString }})
                  </div>
                  <div
                    v-if="myUser?.pendingPlatinumApproval && myUser?.platinumPlanIdToGiveAfterPlatinumApproval && myUser?.verificationInfo1Data && !myUser?.promptToVerify"
                    class="black-text"
                  >
                    Free Platinum membership in review
                  </div>
                  <div
                    v-else-if="myUser?.pendingPlatinumApproval && !myUser?.platinumPlanIdToGiveAfterPlatinumApproval"
                    class="black-text"
                  >
                    Platinum membership in review
                  </div>
                </div>
              </template>

              <!-- For Restaurants -->
              <template v-else-if="myUser?.primaryGroupType == 'restaurant'">
                <div class="d-flex flex-column align-center">
                  <div
                    v-if="myUser?.isPlatinum"
                    class="black-text mb-0"
                  >
                    provided complimentary by RM
                  </div>
                  <div
                    v-if="myUser?.primaryGroupPermission == 'adminStandard'"
                    class="black-text expires-line"
                  >
                    - Admin Only
                  </div>
                </div>
              </template>

              <!-- For Groups -->
              <template v-else-if="myUser?.isPlatinumGroup">
                <div class="d-flex flex-column align-center">
                  <div
                    v-if="myUser?.primaryGroupPermission == 'adminStandard'"
                    class="black-text expires-line"
                  >
                    (Admin Only)
                  </div>
                  <div
                    v-if="myUser?.platinumRenewalDate"
                    class="black-text mb-0"
                  >
                    valid through {{ platinumExpirationDateString }}
                  </div>
                </div>
              </template>

              <!-- For Individuals -->
              <template v-else-if="myUser?.isPlatinumIndividual">
                <div class="d-flex flex-column align-center">
                  <div
                    v-if="myUser?.primaryGroupPermission == 'adminStandard'"
                    class="black-text expires-line"
                  >
                    (Admin Only)
                  </div>
                  <div
                    v-if="myUser?.platinumRenewalDate"
                    class="black-text mb-0"
                  >
                    valid through {{ platinumExpirationDateString }}
                  </div>
                </div>
              </template>

              <div
                v-if="myUser?.primaryAccount"
                class="d-flex flex-column align-center"
              >
                <RouterLink
                  :to="`/m/${myUser.primaryAccount.profileId}`"
                  style="text-decoration: none;"
                >
                  Primary member: {{ myUser?.primaryAccount.fullName }}
                </RouterLink>
              </div>

              <template v-if="myUser.isPlatinumGroup && myUser?.primaryGroupType != 'restaurant'">
                <div class="d-flex flex-column align-center">
                  <span
                    v-if="myUser?.primaryGroupPermission == 'owner' && myUser?.platinumSubscriptionsByState?.current?.groupOwnerMessage"
                    class="black-text account-card-detail"
                  >
                    {{ myUser?.platinumSubscriptionsByState?.current?.groupOwnerMessage }}
                  </span>
                  <span
                    v-else-if="myUser?.primaryGroupPermission == 'owner'"
                    class="black-text account-card-detail"
                  >
                    Account Owner
                  </span>
                  <span
                    v-else-if="!(myUser?.primaryGroupPermission == 'owner' && currentNumberOfPaidSeats == 1) && myUser?.membershipType != 'Trial' && myUser?.platinumSubscriptionsByState?.current?.sourceGroupMembershipMessage"
                    class="black-text account-card-detail"
                  >
                    {{ myUser?.platinumSubscriptionsByState?.current?.sourceGroupMembershipMessage }}
                  </span>
                  <span
                    v-else-if="myUser?.membershipType != 'Trial' && myUser?.platinumSubscriptionsByState?.current?.sourceGroupMembershipMessage"
                    class="black-text account-card-detail"
                  >
                    {{ myUser?.platinumSubscriptionsByState?.current?.sourceGroupMembershipMessage }}
                  </span>
                </div>
              </template>
            </VCol>
          </VRow>
        </div>
      </div>
    </section>

    <VDivider thickness="2" />
    <section class="">
      <div class="content-wrapper text-center">
        <!--
          <div v-if="myUser?.primaryAccount && myUser?.isPlatinum" class="plans-chart flex-container">
          <div class="mb-2 mt-2">Benefits</div>
          <div class="text-xs">
          -Unlimited <span class="text-primary cursor-pointer" @click="isShowingPreferredRequestInfoDialog = true">Preferred Reservations</span> + Event bookings at favorites globally<br>
          -Secondary account for household member<br>
          -Admin account for EA/Manager<br>
          -Discover friends/members' favorites & just booked<br>
          -Track favorites & get notified of happenings<br>
          -Quickly find reservations at favorites across platforms: OpenTable, Resy, etc<br>
          -Verified profile, if applicable, to enhance/expedite access - available for:<br>
          High profile artists/athletes<br>
          Members of select groups - YPO, TIGER 21, Long Angle & alike<br>
          Clients of HNW/VIP groups - Gaming, Travel, Wealth Management, etc.<br>
          inquire now by emailing <a href="mailto:preferred@reserverm.com" target="_blank">preferred@reserverm.com</a>
          </div>
          </div> 
        -->

        <div
          v-if="myUser?.primaryAccount"
          class="plans-chart flex-container"
        >
          <div
            v-if="myUser?.membershipType == 'Standard'"
            class="text-xs"
          />
        </div>


        <div
          v-else
          class="plans-chart flex-container"
        >
          <!-- <div class="mb-2 mt-2">Tiers & Benefits</div> -->

          <!--
            ******************************************************************************************
            ************************************* PLATINUM *********************************
            ******************************************************************************************
          -->
          <div
            class="mb-4"
            :style="{ order: divOrderPlatinumGroup }"
          >
            <div><b>Platinum</b></div>
            <div
              v-if="myUser?.isPlatinum"
              class="text-caption"
              style="line-height: 4px;"
            />

            <div
              v-if="!myUser?.platinumSubscriptionsByState?.pending"
              class="mt-2"
            >
              <div
                v-if="optionsForPlatinum && platinumSelection && !myUser?.isPlatinumGroup"
                class="text-center mb-2"
              >
                <span class="plan-price">{{ platinumSelection.price }}/year<template v-if="isExtensionEligible"> (Preferred rate)</template></span>
                <div
                  v-if="isExtensionEligible && extensionRateExpirationDateString"
                  class="text-xs mt-1"
                >
                  Preferred rate available through {{ extensionRateExpirationDateString }}
                </div>
              </div>

              <template v-if="myUser && myUser.isPlatinumIndividual">
                <div>
                  <VBtn
                    class="plan-button"
                    color="primary"
                    density="comfortable"
                    @click="requestUpgrade(platinumSelection.productId, platinumSelection.priceDollars, null, null, `purchased on ${moment().tz('America/Chicago').format('M/D/YY')}`, true)"
                  >
                    Extend
                  </VBtn>
                  <div
                    v-if="platinumExtendsToMoment"
                    class="text-caption mt-1"
                  >
                    Extends through {{ platinumExtendsToMoment.format("M/D/YY") }}<br>
                    (Post {{ extensionRateExpirationDateString }}, rate is ${{ REGULAR_ANNUAL_PRICE_DOLLARS }}/year)
                  </div>
                </div>
              </template>
              <template
                v-else-if="myUser && (myUser?.membershipType == 'Standard' || myUser?.membershipType == 'Trial' || myUser?.isPlatinumIndividual)"
              >
                <div v-if="myUser?.pendingPlatinumApproval">
                  <VBtn
                    class="plan-button"
                    color="primary"
                    density="comfortable"
                    disabled
                  >
                    Request Pending
                  </VBtn>
                </div>
                <div v-else>
                  <VBtn
                    class="plan-button"
                    color="primary"
                    density="comfortable"
                    @click="requestUpgrade(platinumSelection.productId, platinumSelection.priceDollars, null, null, `purchased on ${moment().tz('America/Chicago').format('M/D/YY')}`)"
                  >
                    <VIcon
                      class="mr-2"
                      icon="mdi-arrow-up-circle"
                    />
                    Request Platinum
                  </VBtn>
                </div>
              </template>
              <div class="mb-4" />
            </div>

            <!--
              <div class="text-xs mt-4">
              <u>Benefits (includes all benefits of Free):</u><br>
              -Unlimited <span class="text-primary cursor-pointer" @click="isShowingPreferredRequestInfoDialog = true">Preferred Reservations</span> + Event bookings<br>
              (request favorites globally)<br>
              -Secondary account for household member<br>
              -Admin account for EA/Manager<br>
              -<v-icon size="18" class="verified-icon ml-1 mr-0" style="margin: 0px; margin-top: -4px; color: rgb(0, 149, 246);">material-symbols:verified</v-icon> profile to boost access (subject to review) for:<br>
              Artists, Athletes, Team Execs/Owners<br>
              Members/clients of select groups including:<br>
              YPO, TIGER 21, VIPs Gaming/Travel, HNW/UHNW clients<br>
              request here <a href="mailto:preferred@reserverm.com" target="_blank">preferred@reserverm.com</a>
              </div> 
            -->
            <!-- loop through all myUser.allSubscriptions -->
            <div
              v-if="myUser?.platinumSubscriptionsByState.current"
              class="text-xs mt-4"
            >
              <div><u>Current & Prior Subscriptions</u></div>

              <!-- current subscription -->
              <div v-if="myUser?.platinumSubscriptionsByState.current">
                <span class="font-weight-bold">Current:</span>
                {{ moment(myUser.platinumSubscriptionsByState.current.firstBillingDate).format("M/D/YY") }} - {{
                  moment(myUser.platinumSubscriptionsByState.current.paidThroughDate).format("M/D/YY") }} - {{
                  currentMembershipMessage }}
              </div>

              <!-- upcoming subscriptions — every one, soonest first -->
              <div
                v-for="subscription in upcomingSubscriptions"
                :key="subscription.id || subscription.firstBillingDate"
              >
                <span class="font-weight-bold">Upcoming:</span>
                {{ moment(subscription.firstBillingDate).format("M/D/YY") }} - {{
                  membershipEndDate(subscription)?.format("M/D/YY") || "?" }}<template
                  v-if="membershipMessage(subscription)"
                >
                  - {{ membershipMessage(subscription) }}
                </template>
              </div>

              <!-- prior subscriptions -->
              <div
                v-for="subscription in priorSubscriptions"
                :key="subscription.id || subscription.firstBillingDate"
              >
                {{ moment(subscription.firstBillingDate).format("M/D/YY") }} - {{
                  moment(subscription.paidThroughDate).format("M/D/YY") }}
              </div>
            </div>
          </div>

          <!--
            ******************************************************************************************
            ****************************** FREE/STANDARD *********************************
            ******************************************************************************************
          -->
        </div>
      </div>
    </section>
  </div>
</template>

<style lang="scss" scoped>
@use "@core/scss/pages/misc.scss";

.misc-footer-tree {
  inline-size: 15.625rem;
  inset-block-end: 3.5rem;
  inset-inline-start: 0.375rem;
}

.plans-chart {
  font-family: var(--ds-font-body);
  color: var(--ds-text-strong);
  text-align: center;
}

.plans-chart .plan {
  margin: 10px;
  width: 23%;
  position: relative;
  float: left;
  background-color: var(--ds-surface-card);
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.plans-chart * {
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
}

.plans-chart header {
  position: relative;
}

.plans-chart .plan-title {
  top: 0;
  padding: 6px 15px;
  -webkit-transform: translateY(-50%);
  transform: translateY(-50%);
  display: inline-block;
  background-color: grey;
  color: #ffffff;
}

.plans-chart .plan-button {
  top: 0px;
}

.plans-chart .plan-info {
  margin: 10px;
}

.plans-chart .price-line {
  position: relative;
  top: 0px;
}

.your-plan-section {
  position: relative;
  top: 0px;
}

.plans-chart .your-plan-line {
  font-size: 1.4em;
  position: relative;
  top: -12px;
  color: rgb(var(--v-theme-primary));
}

.plans-chart .expires-line {
  font-size: 0.9em;
  position: relative;
  top: -18px;
  color: rgb(var(--v-theme-primary));
}

.plans-chart .plan-name-line {
  font-size: 1.4em;
  font-weight: 800;
  text-transform: uppercase;
}

.plans-chart .plan-type-line {
  font-size: 0.9em;
  margin-top: -5px;
}

.plans-chart .plan-cost {
  padding: 0px 10px 10px;
}

.plans-chart .plan-price {
  font-family: var(--ds-font-body);
  font-weight: 800;
  font-size: 1.0em;
  color: #34495e;
}

.plans-chart .plan-type {
  position: relative;
  top: 5px;
  opacity: 0.6;
}

.plans-chart .plan-features {
  padding: 0;
  margin: 0;
  text-align: center;
  list-style: outside none none;
  font-size: 0.875rem;
  line-height: 1.25rem;
}

.plans-chart .plan-features li {
  border-top: 1px solid var(--ds-border-default);
  padding: 10px 5%;
}

.plans-chart .plan-features li:nth-child(even) {
  background: rgba(0, 0, 0, 0.08);
}

.plans-chart .plan-features i {
  margin-right: 8px;
  opacity: 0.4;
}

.plans-chart .plan-select {
  border-top: 1px solid var(--ds-border-default);
  padding: 10px 10px 0;
}

.plans-chart .plan-select a {
  background-color: rgb(var(--v-theme-secondary));
  color: #ffffff;
  text-decoration: none;
  padding: 0.5em 1em;
  -webkit-transform: translateY(50%);
  transform: translateY(50%);
  font-weight: 800;
  text-transform: uppercase;
  display: inline-block;
}

.plans-chart .plan-select a:hover {
  background-color: rgb(var(--v-theme-primary));
}

.plans-chart .featured {
  margin-top: -10px;
  background-color: rgb(var(--v-theme-primary));
  color: #ffffff;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.4);
  z-index: 1;
}

.plans-chart .featured .plan-title,
.plans-chart .featured .plan-price {
  color: #ffffff;
}

.plans-chart .featured .plan-features li {
  border-top: 1px solid rgba(255, 255, 255, 0.4);
}

.plans-chart .featured .plan-select {
  padding: 20px 10px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.4);
}

//Set this back to 767 for mobile/desktop toggling
@media only screen and (max-width: 2767px) {
  .plans-chart .plan {
    width: 90%;
    margin-bottom: 30px;
  }

  .plans-chart .plan-title,
  .plans-chart .plan-select a {
    -webkit-transform: translateY(0);
    transform: translateY(0);
  }

  .plans-chart .plan-cost,
  .plans-chart .featured .plan-cost {
    padding: 5px 5px 5px;
  }

  .plans-chart .plan-select,
  .plans-chart .featured .plan-select {
    padding: 10px 10px 10px;
  }

  .plans-chart .featured {
    margin-top: 0;
  }
}

.smaller-text {
  font-size: 0.8em;
}

.flex-container {
  display: flex;
  flex-direction: column;
}

/* Add loading styles if needed */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 0vh;
}

.back-button-container {
  padding: 0 4px;
}

.back-button {
  padding: 8px 12px;
  color: rgb(51, 51, 51);
  text-decoration: none;
  font-size: 14px;
  transition: background-color 0.2s;
  border-radius: 4px;
  display: inline-flex;
}

.back-button:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.back-button svg {
  color: rgb(102, 102, 102);
}
</style>

<route lang="yaml">
meta:
  layout: website
  action: read
  subject: Auth
  allowLoggedInOnly: true
</route>
